import type { AnswerSuggestion, DocumentSummary, QuestionDefinition } from '@cyberscore/contracts';
import { Download, FileText, Loader2, Paperclip, Upload, X } from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { ApiError } from '../../lib/api';
import { useToast } from '../../ui/Toasts';
import { downloadDocument, useDocuments, useUpload } from './api';
import {
  acceptAttr,
  acceptedKinds,
  documentState,
  fileIds,
  fileProblem,
  megabytes,
  withFile,
  withoutFile,
} from './files';

/** The core's reason for refusing an upload, in words a person can act on. */
export function uploadError(e: unknown): string {
  if (!(e instanceof ApiError)) return e instanceof Error ? e.message : 'Couldn’t upload that';
  if (e.status === 422 && /malware|scan/i.test(e.message)) return 'The malware scan found something in that file, so it wasn’t stored.'; // prettier-ignore
  if (e.status === 503)
    return 'Uploads are paused for a moment (the malware scan is unavailable). Try again shortly.';
  return e.issues[0]?.message ?? e.message;
}

/** Choose or drop one file. Checks it first, so a file the core would refuse isn't sent. */
export function DropZone({
  accept,
  busy,
  disabled,
  onFile,
  label = 'Choose a file',
  hint,
}: {
  accept?: readonly string[];
  busy: boolean;
  disabled?: boolean;
  onFile: (f: File) => void;
  label?: string;
  hint?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const take = (f: File | undefined) => {
    if (!f) return;
    const p = fileProblem(f, accept);
    setProblem(p);
    if (!p) onFile(f);
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    if (!disabled && !busy) take(e.dataTransfer.files[0]);
  };
  return (
    <div
      className={`dropzone${over ? ' over' : ''}${disabled ? ' off' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={drop}
    >
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        accept={acceptAttr(accept)}
        onChange={(e) => {
          take(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      <button type="button" className="btn" disabled={disabled || busy} onClick={() => input.current?.click()}>
        {busy ? <Loader2 size={15} className="spin" /> : <Upload size={15} />} {busy ? 'Uploading and scanning…' : label}
      </button>
      <span className="muted small">{hint ?? `or drop it here. ${acceptedKinds(accept)}, up to 25 MB.`}</span>
      {problem && (
        <div className="error full" role="alert">
          {problem}
        </div>
      )}
    </div>
  ); // prettier-ignore
}

export function DocumentRow({ d, onRemove }: { d: DocumentSummary; onRemove?: () => void }) {
  const st = documentState(d);
  const toast = useToast();
  return (
    <li className="doc-row">
      <FileText size={18} className="muted" />
      <div className="grow">
        <div className="doc-name">{d.file_name}</div>
        <div className="muted small">
          {megabytes(d.size_bytes)} · <span className={st.tone === 'neutral' ? undefined : `${st.tone}-t`}>{st.label}</span>
        </div>
      </div>
      {d.status === 'stored' && (
        <button
          type="button"
          className="btn btn-sm btn-ghost btn-icon"
          title={`Download ${d.file_name}`}
          aria-label={`Download ${d.file_name}`}
          onClick={() => downloadDocument(d).catch((e: Error) => toast(e.message, 'bad'))}
        >
          <Download size={15} />
        </button>
      )}
      {onRemove && (
        <button type="button" className="btn btn-sm btn-ghost btn-icon" title="Remove from this answer" aria-label={`Remove ${d.file_name} from this answer`} onClick={onRemove}>
          <X size={15} />
        </button>
      )}
    </li>
  ); // prettier-ignore
}

/**
 * The input for a file question (task 40): upload a file, or attach one already uploaded to this
 * assessment. The answer is the list of attached documents; saving it is the form's own Save.
 */
export function FileAnswer({
  assessmentId,
  question,
  q,
  value,
  onChange,
  disabled,
}: {
  assessmentId: string;
  question: string;
  q: QuestionDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}) {
  const docs = useDocuments(assessmentId);
  const upload = useUpload(assessmentId);
  const [error, setError] = useState<string | null>(null);
  const settings = q.settings as { accept: string[]; max_files: number };
  const ids = fileIds(value);
  const byId = new Map((docs.data ?? []).map((d) => [d.id, d]));
  const others = (docs.data ?? []).filter((d) => d.status === 'stored' && !ids.includes(d.id) && fileProblem({ name: d.file_name, size: d.size_bytes }, settings.accept) === null); // prettier-ignore
  const full = ids.length >= settings.max_files;

  return (
    <div className="stack-sm">
      {ids.length > 0 && (
        <ul className="doc-list">
          {ids.map((docId) => {
            const d = byId.get(docId);
            return d ? (
              <DocumentRow key={docId} d={d} onRemove={disabled ? undefined : () => onChange(withoutFile(value, docId))} />
            ) : (
              <li key={docId} className="doc-row muted small">
                <Paperclip size={16} /> A file you can’t see any more
              </li>
            );
          })}
        </ul>
      )}
      {!full && (
        <DropZone
          accept={settings.accept}
          busy={upload.isPending}
          disabled={disabled}
          label={ids.length ? 'Add another file' : 'Choose a file'}
          onFile={async (file) => {
            setError(null);
            try {
              const r = await upload.mutateAsync({ file, question });
              onChange(withFile(value, r.document.id, settings.max_files));
            } catch (e) {
              setError(uploadError(e));
            }
          }}
        />
      )}
      {!full && others.length > 0 && (
        <label className="field">
          <span className="muted small">Or attach a file already uploaded to this assessment</span>
          <select
            className="input"
            value=""
            disabled={disabled}
            onChange={(e) => e.target.value && onChange(withFile(value, e.target.value, settings.max_files))}
          >
            <option value="">Choose a file…</option>
            {others.map((d) => (
              <option key={d.id} value={d.id}>
                {d.file_name}
              </option>
            ))}
          </select>
        </label>
      )}
      {settings.max_files > 1 && (
        <span className="muted small">
          {ids.length} of up to {settings.max_files} files
        </span>
      )}
      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}
    </div>
  ); // prettier-ignore
}

/** "96 people" from "Kinta schedule.pdf, page 2: “… 96 employees …”". */
export function Quote({ s }: { s: AnswerSuggestion }) {
  return (
    <blockquote className="quote">
      “{s.document.quote}”
      <footer className="muted small">
        {s.document.file_name}, page {s.document.page} · {Math.round(s.confidence * 100)}% sure
      </footer>
    </blockquote>
  );
}
